import { Avatar, Box, Button, IconButton, Stack, TextField, Typography } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useEffect, useMemo, useRef, useState } from "react";
import { v4 } from "uuid";
import { useChatPdVContext } from "../../../../contexts/atendimento/ChatPdVContext";
import { useMoeda } from "../../../../../core/hooks/useMoeda";
import { useSnackbar } from "../../../../../core/contexts/SnackbarContext";
import { RealInput } from "../../../../../core/components/RealInput";
import { IComplemento } from "../../../../../produtos/interfaces/IComplemento";
import { IITemComplemento } from "../../../../../produtos/interfaces/IITemComplemento";
import { IProdutoPedido } from "../../../../interface/IProdutoPedido";
import { calcularPrecoComplemento } from "../../../../utils/calcularPrecoComplemento";
import { IProduto } from "../../../../../produtos/interfaces/IProduto";
import { ComplementoPaperInput } from "../../../../../produtos/components/DrawerPreparadoOperacao/container/ComplementoPaperInput";
import { acaoPrincipal, contorno, cores, limitarLinhas, texto } from "../../identidade";

export const ChatPdVFormPreparado = () => {
    const {
        produtoFormulario,
        voltarTela,
        adicionarProduto,
        editarProduto,
    } = useChatPdVContext();

    const produto = produtoFormulario as IProduto | IProdutoPedido | undefined;
    const produtoPedido = produtoFormulario as IProdutoPedido | undefined;

    // uuidProdutoReferencia só existe em itens do carrinho (IProdutoPedido),
    // não em produtos do catálogo (IProduto)
    const isEdicao = !!(produtoPedido as any)?.uuidProdutoReferencia;

    const snackbar = useSnackbar();
    const { real } = useMoeda();

    const normalizadoRef = useRef<boolean>(false);

    const [complementos, setComplementos] = useState<IComplemento[]>(() => {
        const fonte = (produtoPedido?.complementos ?? produto?.complementos ?? []) as IComplemento[];
        if (isEdicao) return fonte;
        // Ao adicionar novo item, começar com itensSelecionados vazios
        return fonte.map(c => ({ ...c, itensSelecionados: [] }));
    });
    const [observacao, setObservacao] = useState<string | undefined>(produtoPedido?.observacao);
    const [valorAdicional, setValorAdicional] = useState<any>(produtoPedido?.valorAdicional);

    // Normalizar quantidades dos itens selecionados ao montar
    useEffect(() => {
        if (normalizadoRef.current || !complementos) return;

        let precisaAtualizar = false;
        const complementosNormalizados = complementos.map(complemento => {
            const itensNormalizados = complemento.itensSelecionados?.map(item => {
                if (!item.quantidade || item.quantidade <= 0) {
                    precisaAtualizar = true;
                    return { ...item, quantidade: 1 };
                }
                return item;
            }) ?? [];
            return { ...complemento, itensSelecionados: itensNormalizados };
        });

        normalizadoRef.current = true;
        if (precisaAtualizar) {
            setComplementos(complementosNormalizados);
        }
    }, []);

    const definirItensComplemento = (complementoUuid: string, itens: IITemComplemento[]) => {
        const novosComplementos = [...complementos];
        const index = novosComplementos.findIndex(c => c.uuid === complementoUuid);
        if (index === -1) return snackbar("Complemento não encontrado");
        novosComplementos[index] = { ...novosComplementos[index], itensSelecionados: itens };
        setComplementos(novosComplementos);
    };

    const ajustarQuantidade = (complementoUuid: string, item: IITemComplemento, delta: number) => {
        const novosComplementos = [...complementos];
        const indexC = novosComplementos.findIndex(c => c.uuid === complementoUuid);
        if (indexC === -1) return;

        const complemento = { ...novosComplementos[indexC] };
        const itensSelecionados = [...(complemento.itensSelecionados ?? [])];
        const indexItem = itensSelecionados.findIndex(i => i.uuid === item.uuid);
        if (indexItem < 0) return;

        const novaQuantidade = (itensSelecionados[indexItem].quantidade ?? 1) + delta;
        if (novaQuantidade <= 0) {
            itensSelecionados.splice(indexItem, 1);
        } else {
            itensSelecionados[indexItem] = { ...itensSelecionados[indexItem], quantidade: novaQuantidade };
        }

        complemento.itensSelecionados = itensSelecionados;
        novosComplementos[indexC] = complemento;
        setComplementos(novosComplementos);
    };

    const valorTotal = useMemo(() => {
        let total = produto?.preco ?? 0;
        complementos?.forEach(complemento => {
            total += calcularPrecoComplemento(complemento);
        });
        total += valorAdicional ?? 0;
        return total;
    }, [complementos, valorAdicional, produto?.preco]);

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const erros: string[] = [];
        complementos.forEach(complemento => {
            if (complemento.obrigatorio && (!complemento.itensSelecionados || complemento.itensSelecionados.length === 0)) {
                erros.push(`O complemento ${complemento.descricao} não foi preenchido`);
            }
            if (complemento.obrigatorio && (complemento.itensSelecionados?.length ?? 1) < complemento.numeroMinimoItens) {
                erros.push(`O complemento ${complemento.descricao} precisa de ${complemento.numeroMinimoItens} itens selecionados`);
            }
            if (complemento.obrigatorio && complemento.numeroMaximoItens && (complemento.itensSelecionados?.length ?? 1) > complemento.numeroMaximoItens) {
                erros.push(`O complemento ${complemento.descricao} precisa de no máximo ${complemento.numeroMaximoItens} itens`);
            }
        });

        if (erros.length) {
            return snackbar(erros[0], { severity: "error" });
        }

        if (!produto) return;

        const complementosComQuantidade = complementos.map(complemento => ({
            ...complemento,
            itensSelecionados: complemento.itensSelecionados?.map(item => ({
                ...item,
                quantidade: item.quantidade ?? 1,
            })) ?? [],
        }));

        const dadosProduto: IProdutoPedido = {
            uuidProdutoReferencia: produto.uuid,
            valorAdicional,
            ...produto,
            observacao,
            complementos: complementosComQuantidade,
            uuid: produtoPedido?.uuid ?? v4(),
        };

        if (isEdicao && produtoPedido?.uuid) {
            editarProduto(dadosProduto);
        } else {
            adicionarProduto(dadosProduto);
        }

        voltarTela();
    };

    if (!produto) return null;

    const produtoIProduto = produto as IProduto;

    return (
        <Box
            sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                height: '100%',
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.75,
                    py: 1.5,
                    borderBottom: theme => contorno(theme, 0.09),
                    flexShrink: 0,
                }}
            >
                <IconButton onClick={voltarTela} size="small">
                    <ArrowBack />
                </IconButton>
                {produto.imagens?.length ? (
                    <Avatar
                        alt={produto.descricao}
                        src={produto.imagens[0].url}
                        sx={{ width: 36, height: 36 }}
                    />
                ) : (
                    <Avatar
                        alt={produto.descricao}
                        src={`${process.env.PUBLIC_URL}/images/produtos/generico.png`}
                        sx={{ width: 36, height: 36 }}
                    />
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ ...texto.titulo, lineHeight: 1.35, ...limitarLinhas(2) }}>
                        {produto.descricao}
                    </Typography>
                    <Typography sx={{ fontSize: '13px', fontWeight: 800, color: cores.marca[500], mt: 0.25 }}>
                        {real(valorTotal)}
                    </Typography>
                </Box>
            </Box>

            {/* Form */}
            <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'auto',
                    minHeight: 0,
                }}
            >
                <Box sx={{ flex: 1 }}>
                    {/* Complementos */}
                    {complementos.map(complemento => (
                        <ComplementoPaperInput
                            key={complemento.uuid}
                            complemento={complemento}
                            definirItensComplemento={definirItensComplemento}
                        />
                    ))}

                    {/* Itens selecionados com controle de quantidade */}
                    {complementos.some(c => (c.itensSelecionados?.length ?? 0) > 0) && (
                        <Box sx={{ p: 1.5 }}>
                            <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                                Resumo
                            </Typography>
                            {complementos.map(complemento =>
                                complemento.itensSelecionados?.map(item => (
                                    <Box
                                        key={item.uuid}
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            py: 0.5,
                                        }}
                                    >
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="body2">{item.descricao}</Typography>
                                            <Typography variant="body2" fontWeight="bold">
                                                {real(item.preco * (item.quantidade ?? 1))}
                                            </Typography>
                                        </Box>
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            <Button
                                                size="small"
                                                variant="contained"
                                                onClick={() => ajustarQuantidade(complemento.uuid, item, -1)}
                                                sx={{ minWidth: 28, height: 28, borderRadius: '50%', p: 0, fontWeight: 'bold' }}
                                            >
                                                -
                                            </Button>
                                            <Typography variant="body2">{item.quantidade ?? 1}</Typography>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                onClick={() => ajustarQuantidade(complemento.uuid, item, 1)}
                                                sx={{ minWidth: 28, height: 28, borderRadius: '50%', p: 0, fontWeight: 'bold' }}
                                            >
                                                +
                                            </Button>
                                        </Stack>
                                    </Box>
                                ))
                            )}
                        </Box>
                    )}

                    {/* Observação */}
                    {produtoIProduto.configuracao?.habilitarObservacao && (
                        <Box sx={{ p: 1.5 }}>
                            <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                                Observação
                            </Typography>
                            <Stack gap={1.25}>
                                <TextField
                                    fullWidth
                                    label="Observação"
                                    placeholder="Ex: sem cebola, ponto da carne..."
                                    size="small"
                                    multiline
                                    maxRows={4}
                                    onChange={(e) => setObservacao(e.target.value)}
                                    value={observacao ?? ""}
                                />
                                <TextField
                                    fullWidth
                                    label="Valor adicional"
                                    placeholder="Valor adicional"
                                    size="small"
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValorAdicional(Number(e.target.value))}
                                    value={valorAdicional ?? ""}
                                    InputProps={{
                                        inputComponent: RealInput as any,
                                    }}
                                />
                            </Stack>
                        </Box>
                    )}
                </Box>

                <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    sx={theme => ({
                        ...acaoPrincipal(theme),
                        flexShrink: 0,
                        height: 56,
                        borderRadius: 0,
                        fontSize: '13px',
                    })}
                >
                    {isEdicao ? "Salvar alterações" : "Adicionar ao carrinho"}
                </Button>
            </Box>
        </Box>
    );
};
